<template>
  <div class="search-result-wrap">
    <div class="mobile-close-content" v-if="isMobileSize">
      <span>{{ $t('tokenSearch') }}</span>
      <img :src="closeImg" @click="$mitt.emit('mobile-search-view', false)" alt="">
    </div>
    <div class="search-result-content flex-1-1-h">
      <div class="search-input-wrap">
        <SearchInput
          v-if="searchTokensVisible"
          @getInputVal="onSearchHistoryKey"
          :value="swapMarketSearchInput"
          :placeholder="$t('searchTokens')"
          focus
        />
      </div>
      <div class="search-result-header" v-if="swapMarketSearchInput">
        <div class="search-result-header-left">{{ $t('tokens') }}</div>
        <DropdownSelect
          v-model="selectedSearch"
          :options="networkOptions"
          :icon="selectedSearchIcon"
          :placeholder="$t('allNetworks')"
          :searchPlaceholder="$t('searchNetworks')"
          @change="onSearchChange"
        />
      </div>
      <div class="tokens flex-1-1-h" :class="{ isGap: swapMarketSearchInput }" v-if="!swapMarketSearchInput">
        <div
          class="recent-search-list"
          ref="recentSearchList"
          v-if="searchHistoryKeys && searchHistoryKeys.length"
        >
          <RecentSearch
            :searchHistoryKeys="searchHistoryKeys"
            @delete="onDelete"
            @key="onSearchHistoryKey"
          />
        </div>
        <div class="popular-row">
          <div class="popular-title">{{ $t('popularTokens') }}</div>
          <DropdownSelect
            v-model="selectedHot"
            :options="networkOptions"
            :icon="selectedHotIcon"
            :placeholder="$t('allNetworks')"
            :searchPlaceholder="$t('searchNetworks')"
            @change="onHotChange"
          />
        </div>
        <SearchOptionsHeader isExtra />
        <div
          v-if="filteredHotTokenList.length"
          class="token-list popular-token-list hidden-scrollbar flex-1-1-h"
        >
          <div
            class="search-token-item-wrap"
            v-for="(token, index) in filteredHotTokenList"
            :key="`${token.address}-${index}`"
          >
            <SearchTokenItem
              @onOptional="onOptional"
              @selectSearchedToken="selectSearchedToken"
              :token="token"
            />
            <div class="dividing-line-2" v-if="isMobileSize"></div>
          </div>
        </div>
        <div
          class="loading-no-data-content flex-1-1-h"
          v-else-if="loadingHotToken"
        >
          <div class="loading-wrap">
            <Spinner />
          </div>
        </div>
        <div
          v-else
          class="loading-no-data-content tokens-no-data flex-1-1-h"
        >
          <NoData :isImg="false">
            <img class="no-data-img" :src="searchEmptyImg" alt="" />
          </NoData>
        </div>
      </div>
      <div
        class="tokens hidden-scrollbar flex-1-1-h"
        :class="{ data: swapMarketSearchInput }"
        v-else
      >
        <div
          class="loading-no-data-content flex-1-1-h"
          v-if="swapMarketSearchStatus === 'loading'"
        >
          <div class="loading-wrap">
            <Spinner />
          </div>
        </div>
        <div
          v-else-if="swapMarketSearchStatus === 'noData'"
          class="loading-no-data-content tokens-no-data flex-1-1-h"
        >
            <NoData :isImg="false" :label="$t('noSearchContent')">
            <img class="no-data-img" :src="searchEmptyImg" alt="" />
          </NoData>
        </div>
        <template v-else-if="swapMarketSearchStatus === 'data'">
          <div v-if="sortedSwapMarketTokenKeys.length">
            <div
              v-for="(key, index) in sortedSwapMarketTokenKeys"
              :key="`search-network-${index}`"
            >
              <div class="tokens-title">{{ key }}</div>
              <SearchOptionsHeader v-if="!isMobileSize" />
              <div class="token-list result-token-list">
                <div
                  class="search-token-item-wrap"
                  v-for="(token, index2) in sortedSwapMarketTokenMap[key]"
                  :key="`${token.address}-${index2}`"
                >
                  <SearchTokenItem
                    marketValueList
                    @onOptional="onOptional"
                    @selectSearchedToken="selectSearchedToken"
                    :token="token"
                  />
                  <div class="dividing-line-2" v-if="isMobileSize"></div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="loading-no-data-content tokens-no-data flex-1-1-h">
            <NoData :isImg="false" :label="$t('noSearchContent')">
              <img class="no-data-img" :src="searchEmptyImg" alt="" />
            </NoData>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SearchTokenItem from "../common/SearchTokenItem.vue";
import SearchInput from "../common/SearchInput.vue";
import RecentSearch from "../common/RecentSearch.vue";
import Spinner from "../loading/Spinner.vue";
import SearchOptionsHeader from "./SearchOptionsHeader.vue";
import NoData from "../common/NoData.vue";
import DropdownSelect from "../common/DropdownSelect.vue";

import searchHistory from "../../mixins/searchHistory";

// import { CHAIN_INFO_MAP } from "../../constants";

export default {
  props: {
    searchTokensVisible: {
      type: Boolean
    }
  },
  components: { Spinner, SearchTokenItem, SearchInput, RecentSearch, SearchOptionsHeader, NoData, DropdownSelect },
  data() {
    return {
      swapMarketSearchStatus: "",
      swapMarketSearchInput: "",
      swapMarketTokenMap: {},
      debouncerSearch: null,
      recentSearchListHeight: 0,
      offsetHeight: 0,
      selectedHot: -1,
      selectedSearch: -1
    };
  },
  mixins: [searchHistory],
  computed: {
    ...mapGetters([
      "isDarkMode",
      "isMobileSize",
      "localOptionalList",
      "utc_type",
      "deviceSize",
      "blockchainList"
    ]),
    closeImg() {
      return require(`../../assets/dialog/close-modal${this.darkModeImg}.png`);
    },
    defaultNetworkIcon() {
      return require(`../../assets/common/network.png`);
    },
    selectedHotIcon() {
      const opt = Array.isArray(this.networkOptions)
        ? this.networkOptions.find(item => item.hid === this.selectedHot)
        : null;
      if (opt && opt.hid !== -1 && opt.icon_url) return opt.icon_url;
      return this.defaultNetworkIcon;
    },
    selectedSearchIcon() {
      const opt = Array.isArray(this.networkOptions)
        ? this.networkOptions.find(item => item.hid === this.selectedSearch)
        : null;
      if (opt && opt.hid !== -1 && opt.icon_url) return opt.icon_url;
      return this.defaultNetworkIcon;
    },
    networkOptions() {
      const list = Array.isArray(this.blockchainList)
        ? this.blockchainList.map(item => ({
          hid: item.hid,
          value: item.hid,
          title: item.title,
          icon_url: item.icon,
        }))
        : [];
      return [{ hid: -1, value: -1, title: this.$t('allNetworks'), icon_url: this.defaultNetworkIcon }, ...list];
    },
    filteredHotTokenList() {
      const list = Array.isArray(this.hotTokenListAll) ? this.hotTokenListAll : this.hotTokenList;
      if (this.selectedHot === -1 || this.selectedHot === undefined || this.selectedHot === null) {
        return list.slice(0, 20).map((token, index) => ({ ...token, order: index + 1 }));
      }
      const selectedId = String(this.selectedHot);
      return list
        .filter(token => String(token?.blockchain_id) === selectedId)
        .slice(0, 20)
        .map((token, index) => ({ ...token, order: index + 1 }));
    },
    filteredSwapMarketTokenMap() {
      const map = this.swapMarketTokenMap && typeof this.swapMarketTokenMap === 'object' ? this.swapMarketTokenMap : {};
      if (this.selectedSearch === -1 || this.selectedSearch === undefined || this.selectedSearch === null) {
        return map;
      }
      const selectedId = String(this.selectedSearch);
      const next = {};
      Object.keys(map).forEach(key => {
        const list = Array.isArray(map[key]) ? map[key] : [];
        const filtered = list.filter(token => String(token?.blockchain_id) === selectedId);
        if (filtered.length) {
          next[key] = filtered;
        }
      });
      return next;
    },
    sortedSwapMarketTokenMap() {
      return this.filteredSwapMarketTokenMap && typeof this.filteredSwapMarketTokenMap === 'object'
        ? this.filteredSwapMarketTokenMap
        : {};
    },
    sortedSwapMarketTokenKeys() {
      return Object.keys(this.sortedSwapMarketTokenMap);
    },
  },
  watch: {
    searchTokensVisible: {
      handler(val) {
        if (val) {
          this.selectedHot = -1;
          this.selectedSearch = -1;
          this.hotTokenList = [];
          this.hotTokenListAll = [];
          this.searchHistoryKeys = this.getSearchHistoryKeys()
          this.getHotSwapTokens();
        } else {
          this.swapMarketSearchInput = "";
        }
      },
      immediate: true
    },
  },
  mounted() {
    this.setRealVH();
    window.addEventListener("resize", this.setRealVH);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.setRealVH);
  },
  created() {},

  methods: {
    onHotChange(option) {
      this.selectedHot = option.hid;
    },
    onSearchChange(option) {
      this.selectedSearch = option.hid;
    },
    setRealVH() {
      document.documentElement.style.setProperty("--real-vh", `${window.innerHeight * 0.01}px`);
    },
    selectSearchedToken(info) {
      this.$mitt.emit("set-search-token-info", { ...info, isSearch: true });
      // this.setSearchHistoryTokens(info); // 统一在 swap.js selectSearchedToken 方法里面处理
      let address = info.address;
      let moveAddress = info.address;
      // if (
      //   CHAIN_INFO_MAP[info.network] &&
      //   CHAIN_INFO_MAP[info.network].isMoveAddress
      // ) {
      //   address = info.address + "::" + info.bl_symbol;
      // }
      if (this.isMobileSize && this.$route.path === "/market") {
        info.address = moveAddress;
        info.price = info.price_usd;
        this.$router.push({
          path: "/marketToken",
          query: { ...info, isSearch: true }
        });
      } else {
        if (this.$route.path == "/market") {
          this.$router.push({
            path: "/",
            // query: { ...info, isSearch: true }
            query: {
              network: info.network,
              address: address,
              symbol: info.symbol,
              bl_symbol: info.bl_symbol,
              blockchain_id: info.blockchain_id
            }
          });
        }
      }
    },
  }
};
</script>

<style lang="less" scoped>
.search-result-wrap {
  -webkit-overflow-scrolling: touch;
  touch-action: none;

  .search-result-content {
    max-width: 960px;
    height: calc(100vh - 240px);
    max-height: 600px;
    overflow-y: auto;
    border-radius: 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;

    .search-input-wrap {
      margin-top: 20px;
      padding: 0 20px;
      /deep/ .search-input {
        background: var(--bg-15);
        padding: 0 8px;
        .input-wrap {
          input {
            &::placeholder {
              color: var(--input-placeholder-1);
            }
          }
        }
      }
    }

    .search-result-header {
      margin: 16px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .search-result-header-left {
        font-weight: 600;
        font-size: 16px;
        color: var(--color-29);
      }
    }

    .token-list {
      border-radius: 0 0 20px 20px;
      margin-top: 8px;
      .search-token-item-wrap {
        .search-token-item {
          padding: 10px 20px;
          &:hover {
            background: var(--hover-bg-1);
          }
        }

        .dividing-line-2 {
          margin-left: 20px;
        }
      }
    }

    .popular-token-list {
      overflow-y: auto;
    }

    /deep/ .dropdown-select {
      .dropdown-menu {
        .dropdown-search {
          .transit-input {
            padding: 0 12px 0 8px;

            .input-field {
              &::placeholder {
                font-size: 12px;
              }
            }

            .input-clear {
              img {
                width: 14px;
                height: 14px;
              }
            }
          }
        }

        .dropdown-list {
          .dropdown-item {
            .dropdown-item-left {
              span {
                font-size: 14px;
                font-weight: 500;
              }
            }
          }
        }
      }
    }

    .tokens {
      flex: 1 1;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;

      &.isGap {
        gap: 16px 0;
      }
    
      .recent-search-list {
        margin-top: 20px;
        padding: 0 20px;
      }
      .tokens-title {
        font-weight: 600;
        font-size: 16px;
        padding: 0 20px;
        color: var(--color-29);
      }

      .popular-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 20px;
        margin-top: 20px;

        .popular-title {
          font-weight: 600;
          font-size: 16px;
          color: var(--color-29);
        }
      }

      .tokens-no-data {
        flex-direction: column;
        img {
          width: 64px;
          height: 64px;
        }

        .content {
          font-size: 12px;
          margin-top: 10px;
          color: var(--color-27);
        }
      }
    }

    .loading-no-data-content {
      display: flex;
      align-items: center;
      justify-content: center;
      .loading-wrap {
        width: 24px;
        height: 24px;
      }
    }
  }

  .mobile-close-content {
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 10px 16px 0;
    span {
      font-size: 14px;
      font-weight: 500;
      color: var(--color-29);
    }

    img {
      width: 16px;
      height: 16px;
    }
  }
}

@media screen and (max-width: 1440px) {
  .search-result-wrap {
    .search-result-content {
      height: calc(100vh - 120px);
    }
  }
}

@media screen and (max-width: 680px) {
  .search-result-wrap {
    height: calc(var(--real-vh) * 100);
    display: flex;
    flex-direction: column;

    .search-result-content {
      max-height: none;
      height: 100%;
      .search-input-wrap {
        margin-top: 20px;
      }
      .token-list {
        .search-token-item-wrap {
          .search-token-item {
            padding: 12px 20px;
          }
        }
      }
      
      .tokens {
        .tokens-title {
          font-size: 14px;
        }
      }
    }
  }
}
</style>
